/**
 * Os modelos de mensagem do WhatsApp (H19b).
 *
 * ── Por que esta tela precisou existir ────────────────────────────────────
 *
 * Fora da janela de 24 h a Meta não entrega texto livre: só modelo aprovado.
 * O produto já sabia ESCOLHER um — a caixa "Mensagens prontas" do Atendimento
 * — e não tinha por onde CADASTRAR.
 *
 * O resultado, medido em 11/09/2026: a revenda precisava responder um cliente
 * 24 h depois, abriu a caixa e leu "esta conta ainda não tem mensagens prontas
 * aprovadas. Quem cadastra é o administrador da revenda, no painel do WhatsApp
 * Business". A frase estava certa e não levava a lugar nenhum — nem dizia em
 * QUAL conta cadastrar, que é onde o primeiro modelo costuma se perder.
 *
 * ── O status é o conteúdo, e não um enfeite ──────────────────────────────
 *
 * Um modelo recém-cadastrado não serve para nada até a Meta aprovar, e isso
 * leva de minutos a um dia. Sem o status à vista, quem cadastrou volta ao
 * Atendimento, não vê o modelo e conclui que o cadastro falhou.
 *
 * E a recusa vem com MOTIVO. Mostrá-lo é a diferença entre "Recusado" — que
 * não deixa nada a fazer — e "variável sem exemplo", que a própria pessoa
 * corrige em um minuto.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CanalDeWhatsApp, CategoriaDeModelo, ModeloDeMensagem } from '@cliqcrm/contracts';
import { Botao, Campo, CampoArea, CampoSelecao, Selo, type TomSelo } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import { Estado } from '@/shared/ui/estados/estados';
import { useApi, useModelosDeMensagem } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { SincronizarModelos } from './sincronizar-modelos';
import './modelos-de-mensagem.css';

/** Tetos da Meta, repetidos aqui para a recusa ser imediata e não vir de longe. */
const MAXIMO_DO_CORPO = 1024;
const MAXIMO_DO_RODAPE = 60;

export function ModelosDeMensagem({ canal }: { canal: CanalDeWhatsApp }): ReactNode {
  const consulta = useModelosDeMensagem(canal.referencia_externa);
  const [criando, definirCriando] = useState(false);

  return (
    <div className="mod__corpo">
      <p className="mod__explicacao">
        Passadas 24 h desde a última mensagem do cliente, o WhatsApp só entrega texto que a Meta
        aprovou antes. É isto que você cadastra aqui — e é o que aparece em “Mensagens prontas”, no
        Atendimento.
      </p>

      {/*
          `Estado` recebe uma FUNÇÃO, e não filhos soltos: é ela que separa
          carregando, vazio e erro com um desenho só no produto inteiro.
        */}
      <Estado consulta={consulta} forma="linha" linhas={2}>
        {(dados) =>
          dados.itens.length === 0 ? (
            <p className="mod__vazio">
              Nenhum modelo nesta conta ainda. O primeiro costuma ser uma retomada de atendimento.
            </p>
          ) : (
            <ul className="mod__lista">
              {dados.itens.map((modelo) => (
                <Item key={`${modelo.nome}:${modelo.idioma}`} canal={canal} modelo={modelo} />
              ))}
            </ul>
          )
        }
      </Estado>

      {criando ? (
        <Formulario canal={canal} aoFechar={() => definirCriando(false)} />
      ) : (
        <div className="mod__acoes">
          <Botao miudo onClick={() => definirCriando(true)}>
            Novo modelo
          </Botao>
          {/*
              Aqui também, e não só no cartão do número: quem acabou de ver um
              modelo "Aprovado" nesta lista é quem quer que ele apareça no
              Atendimento agora.
            */}
          <SincronizarModelos canal={canal} />
        </div>
      )}
    </div>
  );
}

/* ── Um modelo ────────────────────────────────────────────────────────────── */

function Item({ canal, modelo }: { canal: CanalDeWhatsApp; modelo: ModeloDeMensagem }): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [erro, definirErro] = useState<string | null>(null);

  const remover = useMutation({
    mutationFn: async () => {
      definirErro(null);
      await api!.removerModeloDeMensagem({
        caminho: { id: canal.referencia_externa, nome: modelo.nome },
      });
    },
    onSuccess: () => {
      void cache.invalidateQueries({
        queryKey: chaves.modelosDeMensagem(canal.referencia_externa),
      });
    },
    onError: (falha: unknown) => {
      definirErro(recadoDeErro(falha, { generica: 'Não foi possível apagar o modelo.' }));
    },
  });

  return (
    <li className="mod__item">
      <div className="mod__item-topo">
        <code className="mod__nome">{modelo.nome}</code>
        <Selo tom={tomDoStatus(modelo.status)} role="status">
          {nomeDoStatus(modelo.status)}
        </Selo>
        <span className="mod__meta">
          {modelo.categoria} · {modelo.idioma}
        </span>
        <Botao
          miudo
          variante="fantasma"
          desabilitado={remover.isPending || api === null}
          motivo={api === null ? 'Sem conexão agora. Tente de novo em instantes.' : 'Apagando…'}
          onClick={() => remover.mutate()}
        >
          Apagar
        </Botao>
      </div>

      <p className="mod__texto">{modelo.corpo}</p>
      {modelo.rodape && <p className="mod__rodape">{modelo.rodape}</p>}

      {/*
        O motivo da recusa, quando existe.

        Sem ele, "Recusado" é um beco: a pessoa não sabe o que corrigir, e o
        motivo mais comum — variável sem exemplo — é justamente o que ela
        conseguiria arrumar sozinha em um minuto.
      */}
      {modelo.motivo_da_recusa && (
        <p className="mod__recusa">
          A Meta recusou: <b>{modelo.motivo_da_recusa}</b>. Apague este modelo e cadastre de novo
          corrigindo o motivo — a Meta não aceita dois modelos com o mesmo nome.
        </p>
      )}

      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
    </li>
  );
}

/* ── Cadastrar ────────────────────────────────────────────────────────────── */

function Formulario({
  canal,
  aoFechar,
}: {
  canal: CanalDeWhatsApp;
  aoFechar: () => void;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const [nome, definirNome] = useState('');
  const [categoria, definirCategoria] = useState<CategoriaDeModelo>('UTILITY');
  const [corpo, definirCorpo] = useState('');
  const [rodape, definirRodape] = useState('');
  const [exemplos, definirExemplos] = useState<string[]>([]);
  const [erro, definirErro] = useState<string | null>(null);

  /*
   * As variáveis saem do TEXTO, e a tela cobra um exemplo para cada.
   *
   * Variável sem exemplo é o motivo de recusa mais comum da Meta, e a recusa
   * chega horas depois sem dizer qual faltou. Aqui o campo simplesmente aparece
   * enquanto a pessoa escreve.
   */
  const variaveis = useMemo(() => contarVariaveis(corpo), [corpo]);

  const criar = useMutation({
    mutationFn: async () => {
      definirErro(null);
      await api!.criarModeloDeMensagem({
        caminho: { id: canal.referencia_externa },
        corpo: {
          nome,
          categoria,
          idioma: 'pt_BR',
          corpo,
          ...(rodape.trim() === '' ? {} : { rodape: rodape.trim() }),
          ...(variaveis === 0 ? {} : { exemplos: exemplos.slice(0, variaveis) }),
        },
      });
    },
    onSuccess: () => {
      void cache.invalidateQueries({
        queryKey: chaves.modelosDeMensagem(canal.referencia_externa),
      });
      aoFechar();
    },
    onError: (falha: unknown) => {
      definirErro(recadoDeErro(falha, { generica: 'Não foi possível cadastrar o modelo.' }));
    },
  });

  const faltaExemplo = Array.from({ length: variaveis }).some(
    (_, i) => (exemplos[i] ?? '').trim() === '',
  );
  const podeEnviar = nome.trim() !== '' && corpo.trim() !== '' && !faltaExemplo;

  return (
    <form
      className="mod__form"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (podeEnviar) criar.mutate();
      }}
    >
      <Campo
        rotulo="Nome"
        rotuloVersal
        value={nome}
        maxLength={512}
        onChange={(e) => definirNome(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
        ajuda="Só minúsculas, números e sublinhado. É o nome interno, e o cliente nunca o vê."
        placeholder="retomada_de_atendimento"
      />

      <CampoSelecao
        rotulo="Categoria"
        rotuloVersal
        value={categoria}
        onChange={(e) => definirCategoria(e.target.value as CategoriaDeModelo)}
        ajuda="Se a Meta discordar, ela reclassifica em vez de recusar."
      >
        <option value="UTILITY">Utilidade — sobre um atendimento em andamento</option>
        <option value="MARKETING">Divulgação — oferta, novidade, convite</option>
      </CampoSelecao>

      <CampoArea
        rotulo="Mensagem"
        rotuloVersal
        rows={3}
        value={corpo}
        maxLength={MAXIMO_DO_CORPO}
        onChange={(e) => definirCorpo(e.target.value)}
        placeholder="Olá, {{1}}! Podemos retomar seu atendimento?"
        ajuda="Use {{1}}, {{2}}… onde entra algo que muda a cada envio, como o nome do cliente."
      />

      {/*
        Um campo por variável, e eles aparecem sozinhos.

        A Meta exige um exemplo por variável e recusa sem eles. O exemplo é só
        para a revisão dela — não é o que vai ser enviado.
      */}
      {variaveis > 0 && (
        <fieldset className="mod__exemplos">
          <legend>Exemplo de cada variável</legend>
          <p className="mod__ajuda">
            A Meta exige um exemplo para revisar o modelo. Ele não é enviado a ninguém.
          </p>
          {Array.from({ length: variaveis }).map((_, i) => (
            <Campo
              key={i}
              rotulo={`{{${i + 1}}}`}
              tamanho="miudo"
              value={exemplos[i] ?? ''}
              onChange={(e) => {
                const novos = [...exemplos];
                novos[i] = e.target.value;
                definirExemplos(novos);
              }}
              placeholder={i === 0 ? 'Marcos' : ''}
            />
          ))}
        </fieldset>
      )}

      <Campo
        rotulo="Rodapé"
        rotuloVersal
        value={rodape}
        maxLength={MAXIMO_DO_RODAPE}
        onChange={(e) => definirRodape(e.target.value)}
        ajuda={`Opcional, até ${MAXIMO_DO_RODAPE} caracteres. Costuma levar o nome da organização.`}
      />

      <div className="mod__form-acoes">
        <Botao
          type="submit"
          miudo
          desabilitado={!podeEnviar || criar.isPending || api === null}
          motivo={
            api === null
              ? 'Sem conexão agora. Tente de novo em instantes.'
              : faltaExemplo
                ? 'Preencha um exemplo para cada variável.'
                : 'Preencha o nome e a mensagem.'
          }
        >
          {criar.isPending ? 'Enviando…' : 'Enviar para aprovação'}
        </Botao>
        <Botao miudo variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
      </div>

      <p className="mod__ajuda">
        A Meta revisa antes de liberar — costuma levar minutos, e o contrato dela fala em até 24 h.
        Enquanto isso o modelo fica como <b>Em análise</b>.
      </p>

      {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
    </form>
  );
}

/* ── Traduções ────────────────────────────────────────────────────────────── */

export function contarVariaveis(corpo: string): number {
  return new Set([...corpo.matchAll(/\{\{\s*(\d+)\s*\}\}/g)].map((c) => Number(c[1]))).size;
}

/**
 * O vocabulário da Meta, em português — `APPROVED` não é palavra de tela.
 *
 * `PENDING` e `IN_REVIEW` são o MESMO estado com dois nomes: a API devolve um e
 * a documentação lista o outro. Mostrá-los diferente inventaria uma distinção
 * que não existe, e a pessoa passaria a procurar o que mudou.
 *
 * O parâmetro é `string`, e não a união dos conhecidos: o status é vocabulário
 * da Meta e ela acrescenta estado sem avisar (A03 §6) — por isso o contrato
 * deixa o campo aberto. O que não se reconhece sai como desconhecido, e não
 * como "Desativado": dizer que um modelo foi desativado quando não se sabe o
 * que ele é seria inventar uma punição.
 */
function nomeDoStatus(status: string): string {
  if (status === 'APPROVED') return 'Aprovado';
  if (status === 'PENDING' || status === 'IN_REVIEW') return 'Em análise';
  if (status === 'REJECTED') return 'Recusado';
  if (status === 'APPEAL_REQUESTED') return 'Em recurso';
  if (status === 'PAUSED') return 'Pausado';
  if (status === 'DISABLED') return 'Desativado';
  return 'Estado desconhecido';
}

function tomDoStatus(status: string): TomSelo {
  if (status === 'APPROVED') return 'ok';
  if (status === 'PENDING' || status === 'IN_REVIEW' || status === 'APPEAL_REQUESTED') {
    return 'atencao';
  }
  if (status === 'REJECTED') return 'erro';
  return 'neutro';
}

/** O resumo que a aba mostra ao lado do nome, sem abrir: "6 aprovados". */
export function resumoDosModelos(itens: readonly ModeloDeMensagem[]): string {
  const aprovados = itens.filter((m) => m.status === 'APPROVED').length;
  // Os dois nomes do mesmo estado contam juntos — senão o resumo diria
  // "nenhum" com um modelo em análise bem na frente de quem está olhando.
  const analisando = itens.filter((m) => m.status === 'PENDING' || m.status === 'IN_REVIEW').length;

  if (itens.length === 0) return 'nenhum cadastrado';
  if (aprovados === 0 && analisando > 0) return `${analisando} em análise`;
  const emAnalise = analisando > 0 ? `, ${analisando} em análise` : '';
  return `${aprovados} aprovado${aprovados === 1 ? '' : 's'}${emAnalise}`;
}
